import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Empty, ErrorBox, Row, Spinner, Toggle } from './ui.js';

interface TeamMember {
  telegramUserId: string;
  telegramId: string;
  displayName: string | null;
  username: string | null;
  fromJira: boolean;
}

interface Team {
  id: string;
  name: string;
  description: string | null;
  jiraGroupName: string | null;
  syncedAt: string | null;
  members: TeamMember[];
}

interface Mandate {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  events: string[];
  projects: string[];
  allMembers: boolean;
  teamIds: string[];
  telegramUserIds: string[];
  teamNames: string[];
  bypassQuietHours: boolean;
  reaches: number;
  targetsNobody: boolean;
}

interface AdminUser {
  telegramUserId: string;
  username: string | null;
  displayName: string | null;
}

export function TeamsTab(): JSX.Element {
  const { t } = useI18n();
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [group, setGroup] = useState('');

  const teams = useQuery({
    queryKey: ['admin-teams'],
    queryFn: () => api<{ teams: Team[] }>('/admin/teams'),
  });

  const groups = useQuery({
    queryKey: ['admin-jira-groups'],
    queryFn: () => api<{ groups: string[] }>('/admin/teams/jira-groups'),
  });

  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api<{ users: AdminUser[] }>('/admin/users?take=100'),
  });

  const invalidate = (): void => {
    void client.invalidateQueries({ queryKey: ['admin-teams'] });
  };

  const create = useMutation({
    mutationFn: () =>
      api('/admin/teams', {
        method: 'POST',
        body: { name, jiraGroupName: group || null },
      }),
    onSuccess: () => {
      setName('');
      setGroup('');
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/teams/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const addMember = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      api(`/admin/teams/${teamId}/members/${userId}`, { method: 'PUT' }),
    onSuccess: invalidate,
  });

  const removeMember = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      api(`/admin/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const sync = useMutation({
    mutationFn: () => api<{ matched: number; skipped: number }>('/admin/teams/sync', { method: 'POST' }),
    onSuccess: invalidate,
  });

  if (teams.isLoading) return <Spinner />;
  if (teams.error) return <ErrorBox error={teams.error} />;

  return (
    <>
      <Card title={t('admin.teams')}>
        <div className="note small">{t('admin.teams_hint')}</div>
        <div className="field">
          <label htmlFor="team-name">{t('admin.team_name')}</label>
          <input
            id="team-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t('admin.team_new')}
          />
        </div>
        <div className="field">
          <label htmlFor="team-group">{t('admin.team_jira_group')}</label>
          <select id="team-group" value={group} onChange={(event) => setGroup(event.target.value)}>
            <option value="">{t('admin.team_jira_group_none')}</option>
            {(groups.data?.groups ?? []).map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
        <div className="btn-row">
          <button
            type="button"
            className="btn"
            disabled={name.trim().length === 0 || create.isPending}
            onClick={() => create.mutate()}
          >
            {t('admin.team_new')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={sync.isPending}
            onClick={() => sync.mutate()}
          >
            {t('admin.team_sync')}
          </button>
        </div>
        <div className="note small">{t('admin.team_sync_note')}</div>
        {create.error ? <ErrorBox error={create.error} /> : null}
        {sync.error ? <ErrorBox error={sync.error} /> : null}
      </Card>

      {(teams.data?.teams ?? []).length === 0 ? <Empty /> : null}

      {(teams.data?.teams ?? []).map((team) => (
        <Card key={team.id} title={team.name}>
          <div className="row-sub">
            {team.jiraGroupName ? (
              <span className="chip">{team.jiraGroupName}</span>
            ) : (
              <span className="chip">{t('admin.team_jira_group_none')}</span>
            )}{' '}
            <span className="muted small">
              {team.jiraGroupName
                ? team.syncedAt
                  ? t('admin.team_synced', { when: new Date(team.syncedAt).toLocaleString() })
                  : t('admin.team_never_synced')
                : t('admin.team_members', { count: team.members.length })}
            </span>
          </div>

          {team.members.map((member) => (
            <Row
              key={member.telegramUserId}
              title={member.displayName ?? member.username ?? member.telegramId}
              subtitle={member.fromJira ? team.jiraGroupName : undefined}
              right={
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    removeMember.mutate({ teamId: team.id, userId: member.telegramUserId })
                  }
                >
                  ✕
                </button>
              }
            />
          ))}

          <div className="field">
            <label htmlFor={`add-${team.id}`}>{t('admin.team_add_member')}</label>
            <select
              id={`add-${team.id}`}
              value=""
              onChange={(event) => {
                if (event.target.value) {
                  addMember.mutate({ teamId: team.id, userId: event.target.value });
                }
              }}
            >
              <option value="">—</option>
              {(users.data?.users ?? [])
                .filter(
                  (user) =>
                    !team.members.some((member) => member.telegramUserId === user.telegramUserId),
                )
                .map((user) => (
                  <option key={user.telegramUserId} value={user.telegramUserId}>
                    {user.displayName ?? user.username ?? user.telegramUserId}
                  </option>
                ))}
            </select>
          </div>

          <button type="button" className="btn btn-danger" onClick={() => remove.mutate(team.id)}>
            {t('common.delete')}
          </button>
        </Card>
      ))}

      <MandatesCard teams={teams.data?.teams ?? []} users={users.data?.users ?? []} />
    </>
  );
}

function MandatesCard({ teams, users }: { teams: Team[]; users: AdminUser[] }): JSX.Element {
  const { t } = useI18n();
  const client = useQueryClient();
  const [draft, setDraft] = useState<null | {
    id?: string;
    name: string;
    events: string[];
    projects: string;
    allMembers: boolean;
    teamIds: string[];
    telegramUserIds: string[];
    bypassQuietHours: boolean;
  }>(null);

  const mandates = useQuery({
    queryKey: ['admin-mandates'],
    queryFn: () => api<{ mandates: Mandate[]; note: string }>('/admin/mandates'),
  });

  const catalog = useQuery({
    queryKey: ['notify-catalog'],
    queryFn: () => api<{ events: Array<{ event: string; category: string }> }>('/notifications/catalog'),
    enabled: draft !== null,
  });

  const invalidate = (): void => {
    void client.invalidateQueries({ queryKey: ['admin-mandates'] });
  };

  const save = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('no draft');
      const body = {
        name: draft.name,
        events: draft.events,
        projects: draft.projects
          .split(',')
          .map((value) => value.trim().toUpperCase())
          .filter(Boolean),
        allMembers: draft.allMembers,
        teamIds: draft.teamIds,
        telegramUserIds: draft.telegramUserIds,
        bypassQuietHours: draft.bypassQuietHours,
      };
      return draft.id
        ? api(`/admin/mandates/${draft.id}`, { method: 'PATCH', body })
        : api('/admin/mandates', { method: 'POST', body });
    },
    onSuccess: () => {
      setDraft(null);
      invalidate();
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api(`/admin/mandates/${id}`, { method: 'PATCH', body: { enabled } }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/mandates/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const toggleIn = (list: string[], value: string): string[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  return (
    <Card title={t('admin.mandates')}>
      <div className="note small">{t('admin.mandates_hint')}</div>
      {mandates.data?.note ? <div className="note small">{mandates.data.note}</div> : null}

      {(mandates.data?.mandates ?? []).map((mandate) => (
        <div key={mandate.id} className="row" style={{ display: 'block' }}>
          <div className="row-title">{mandate.name}</div>
          <div className="row-sub">
            <span className="chip">
              {mandate.allMembers
                ? t('admin.mandate_all_members')
                : [...mandate.teamNames, `${mandate.telegramUserIds.length}👤`]
                    .filter(Boolean)
                    .join('، ')}
            </span>{' '}
            <span className={`chip ${mandate.targetsNobody ? 'chip-danger' : 'chip-done'}`}>
              {mandate.targetsNobody
                ? t('admin.mandate_nobody')
                : t('admin.mandate_reaches', { count: mandate.reaches })}
            </span>
          </div>
          <div className="row-sub muted small">
            {(mandate.events.length === 0 ? [t('notify.rule_any')] : mandate.events).join('، ')} ·{' '}
            {(mandate.projects.length === 0
              ? [t('notify.rule_all_projects')]
              : mandate.projects
            ).join('، ')}
          </div>
          <div className="btn-row" style={{ marginTop: 8 }}>
            <Toggle
              label={t('common.enabled')}
              checked={mandate.enabled}
              onChange={(next) => toggle.mutate({ id: mandate.id, enabled: next })}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() =>
                setDraft({
                  id: mandate.id,
                  name: mandate.name,
                  events: mandate.events,
                  projects: mandate.projects.join(', '),
                  allMembers: mandate.allMembers,
                  teamIds: mandate.teamIds,
                  telegramUserIds: mandate.telegramUserIds,
                  bypassQuietHours: mandate.bypassQuietHours,
                })
              }
            >
              {t('common.edit')}
            </button>
            <button type="button" className="btn btn-danger" onClick={() => remove.mutate(mandate.id)}>
              {t('common.delete')}
            </button>
          </div>
        </div>
      ))}

      {draft === null ? (
        <button
          type="button"
          className="btn"
          onClick={() =>
            setDraft({
              name: '',
              events: [],
              projects: '',
              allMembers: false,
              teamIds: [],
              telegramUserIds: [],
              bypassQuietHours: true,
            })
          }
        >
          {t('admin.mandate_new')}
        </button>
      ) : (
        <>
          <div className="field">
            <label htmlFor="mandate-name">{t('admin.mandate_name')}</label>
            <input
              id="mandate-name"
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          </div>

          <div className="field">
            <label htmlFor="mandate-projects">{t('admin.mandate_projects')}</label>
            <input
              id="mandate-projects"
              className="mono"
              value={draft.projects}
              placeholder="GTM, CSO"
              onChange={(event) => setDraft({ ...draft, projects: event.target.value })}
            />
          </div>

          <div className="field">
            <label>{t('admin.mandate_events')}</label>
            <div className="scroll-x">
              {(catalog.data?.events ?? []).map((item) => (
                <button
                  key={item.event}
                  type="button"
                  className={`chip ${draft.events.includes(item.event) ? 'chip-done' : ''}`}
                  onClick={() => setDraft({ ...draft, events: toggleIn(draft.events, item.event) })}
                >
                  {item.event}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label>{t('admin.mandate_targets')}</label>
            <Toggle
              label={t('admin.mandate_all_members')}
              checked={draft.allMembers}
              onChange={(next) => setDraft({ ...draft, allMembers: next })}
            />
            {draft.allMembers ? null : (
              <>
                <div className="scroll-x">
                  {teams.map((team) => (
                    <button
                      key={team.id}
                      type="button"
                      className={`chip ${draft.teamIds.includes(team.id) ? 'chip-done' : ''}`}
                      onClick={() => setDraft({ ...draft, teamIds: toggleIn(draft.teamIds, team.id) })}
                    >
                      {team.name}
                    </button>
                  ))}
                </div>
                <div className="scroll-x">
                  {users.map((user) => (
                    <button
                      key={user.telegramUserId}
                      type="button"
                      className={`chip ${
                        draft.telegramUserIds.includes(user.telegramUserId) ? 'chip-done' : ''
                      }`}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          telegramUserIds: toggleIn(draft.telegramUserIds, user.telegramUserId),
                        })
                      }
                    >
                      {user.displayName ?? user.username ?? user.telegramUserId}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <Toggle
            label={t('admin.mandate_bypass_quiet')}
            hint={t('admin.mandate_bypass_quiet_hint')}
            checked={draft.bypassQuietHours}
            onChange={(next) => setDraft({ ...draft, bypassQuietHours: next })}
          />

          <div className="btn-row">
            <button
              type="button"
              className="btn"
              disabled={draft.name.trim().length === 0 || save.isPending}
              onClick={() => save.mutate()}
            >
              {t('common.save')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setDraft(null)}>
              {t('common.cancel')}
            </button>
          </div>
          {save.error ? <ErrorBox error={save.error} /> : null}
        </>
      )}
    </Card>
  );
}
